import {
  ArrowRight,
  CalendarCheck,
  CreditCard,
  FileText,
  Files,
  HandCoins,
  Users,
  Wallet,
  Building2,
  KeyRound,
  Layers,
  CheckCircle2,
  CalendarX2,
  Clock,
  TrendingUp,
  EyeOff,
  SearchX,
  Search,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { UserCircle, Settings, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PRICING_TIERS = [
  { label: "Up to 300 members", name: "Starter", monthlyPrice: 49 },
  { label: "Up to 500 members", name: "Growth", monthlyPrice: 79 },
  { label: "Up to 1,000 members", name: "Scale", monthlyPrice: 99 },
  { label: "Up to 5,000 members", name: "Enterprise", monthlyPrice: 149 },
];
const ANNUAL_DISCOUNT = 15;

const PAIN_POINTS = [
  {
    title: "Everything is Scattered",
    description: "Members, giving, and records live across spreadsheets, apps, and notes.",
    icon: Files,
  },
  {
    title: "No Clear Financial Visibility",
    description: "No simple way to track tithes, offerings, and expenses with confidence.",
    icon: HandCoins,
  },
  {
    title: "Too Much Manual Work",
    description: "Hours spent updating spreadsheets and fixing errors.",
    icon: Clock,
  },
  {
    title: "Growth Creates Complexity",
    description: "As your church grows, it becomes harder to stay organized without the right system.",
    icon: TrendingUp,
  },
  {
    title: "No Clear Roles and Access",
    description: "Leaders and volunteers either see too much or not enough.",
    icon: EyeOff,
  },
  {
    title: "No Reliable Records When Needed",
    description: "Hard to find member history, giving records, or past activity quickly.",
    icon: SearchX,
  },
];

const FEATURE_CARDS = [
  {
    title: "All your data in one place",
    description: "Members, finances, and records all organized together.",
    icon: Layers,
  },
  {
    title: "Finance Management",
    description: "Track income and expenses across your church with clarity.",
    icon: Wallet,
  },
  {
    title: "Less work, fewer errors",
    description: "Automate tracking and simplify your workflow.",
    icon: Clock,
  },
  {
    title: "Built to scale with your church",
    description: "Stay organized as you grow.",
    icon: TrendingUp,
  },
  {
    title: "Role-Based Access",
    description: "Give each leader the right level of access and control.",
    icon: KeyRound,
  },
  {
    title: "Instant access to records",
    description: "Find member and giving history in seconds.",
    icon: Search,
  },
  {
    title: "Ministry Management",
    description: "Organize ministries and manage leaders with ease.",
    icon: Building2,
  },
  {
    title: "Events and Attendance",
    description: "Create events and check members in quickly with QR codes.",
    icon: CalendarCheck,
  },
  {
    title: "Giving Statements",
    description: "Generate IRS-ready statements in one click.",
    icon: FileText,
  },
];

const PLAN_FEATURES = [
  "Ministry management",
  "Fund based accounting",
  "Unlimited transactions",
  "Role-based access",
  "Giving statements",
];

const FAQ_ITEMS = [
  {
    question: "How long does setup take?",
    answer: "Most churches can get started the same day by importing members and inviting leaders in minutes.",
  },
  {
    question: "Can we switch plans as our church grows?",
    answer: "Yes. You can switch plans anytime. Choose the plan that matches your current member size.",
  },
  {
    question: "Do all plans include the same features?",
    answer: "Yes. All plans include the same core platform features. Pricing changes by member count.",
  },
  {
    question: "How does yearly billing work?",
    answer: "When yearly is selected, pricing is billed annually with a 15% savings compared to monthly billing.",
  },
  {
    question: "Can multiple leaders use the system?",
    answer: "Yes. You can invite leaders and staff and control access using role-based permissions.",
  },
  {
    question: "What support is available?",
    answer: "You can contact our team at contact@ichurchdata.com for account, billing, and platform support.",
  },
];

const TESTIMONIAL_CHURCHES = [
  {
    name: "Kansas Chin Baptist Church",
    logo: "/church-logos/kansas-chin-baptist-church.jpg",
    quote: "iChurch Data helps our team keep member records and attendance organized every week.",
  },
  {
    name: "Chin Christian Mission Church",
    logo: "/church-logos/chin-christian-mission-church.jpg",
    quote: "Simple finance tracking and ministry management in one clean dashboard.",
  },
  {
    name: "Immanuel Chin Christian Church",
    logo: "/church-logos/immanuel-chin-christian-church.jpg",
    quote: "Our staff can finally manage people, events, and giving without switching tools.",
  },
];

const LandingPage = () => {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = !!user;
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(3); // 5,000 members plan selected by default
  const displayName = user?.email?.split("@")[0] || "User";
  const userInitials = (displayName.slice(0, 2) || "ME").toUpperCase();

  return (
    <div className="min-h-screen brand-page-bg text-slate-900">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-0 h-96 w-96 rounded-full bg-[#dbe9ff] blur-3xl" />
        <div className="absolute top-20 right-0 h-80 w-80 rounded-full bg-[#f8efe2] blur-3xl" />
        <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[#e6f4ee] blur-3xl" />
      </div>

      <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-200/70 bg-[#fbfdff]/92 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/landing_icd_logo.png" alt="iChurch Data" className="h-8 w-8 rounded-xl object-cover" />
            <span className="text-lg font-semibold tracking-tight text-slate-900">iChurch Data</span>
          </Link>

          <div className="flex items-center gap-1 text-sm font-medium text-slate-600">
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <a href="#features">Features</a>
            </Button>
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <a href="#pricing">Pricing</a>
            </Button>
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/about">About</Link>
            </Button>
            {isAuthenticated ? (
              <>
                <Button variant="ghost" className="text-slate-700" asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-secondary transition-colors" aria-label="Open profile menu">
                      <Avatar className="h-9 w-9 border-2 border-border">
                        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium text-foreground">{displayName}</p>
                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                        {role && <Badge variant="secondary" className="w-fit text-xs capitalize mt-1">{role}</Badge>}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                      <UserCircle className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => signOut()}
                      className="cursor-pointer text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" className="text-slate-700" asChild>
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button className="bg-[#1e3a8a] hover:bg-[#162d70]" asChild>
                  <Link to="/register">Sign Up</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-[69px]">
        <section className="mx-auto grid w-full max-w-6xl gap-12 px-6 pb-12 pt-14 md:grid-cols-2 md:pt-20">
          <div className="space-y-4">
            <h1 className="max-w-xl text-3xl font-semibold leading-tight tracking-tight text-[#1e3a8a] md:text-5xl">
              The simplest way to manage your church
            </h1>
            <p className="max-w-xl text-lg leading-relaxed text-slate-600">
              iChurch Data helps small and mid size churches manage members and finances 
              in one simple place without the complexity of traditional church software.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="bg-[#1e3a8a] px-7 hover:bg-[#162d70]" asChild>
                <Link to={isAuthenticated ? "/dashboard" : "/register"}>
                  {isAuthenticated ? "Go to dashboard" : "Get started"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-slate-300 px-7 text-slate-700" asChild>
                <Link to={isAuthenticated ? "/profile" : "/login"}>
                  {isAuthenticated ? "My profile" : "Sign in"}
                </Link>
              </Button>
            </div>
          </div>

          <div className="rounded-3xl border border-[#d7e4f5] bg-gradient-to-br from-white to-[#f4f8ff] p-1 shadow-[0_20px_60px_-24px_rgba(2,6,23,0.25)]">
            <div className="grid grid-cols-2 gap-3 rounded-2xl bg-gradient-to-br from-[#f7faff] to-[#f8f5ef] p-4">
              <div className="rounded-xl border border-slate-200 bg-white/95 p-3">
                <p className="text-xs font-medium text-slate-500">Member list</p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between gap-1 rounded-lg bg-slate-50 px-2 py-1.5 text-xs">
                    <span className="truncate">Sarah Kim</span><span className="shrink-0 text-slate-500">Family A</span>
                  </div>
                  <div className="flex items-center justify-between gap-1 rounded-lg bg-slate-50 px-2 py-1.5 text-xs">
                    <span className="truncate">David Chin</span><span className="shrink-0 text-slate-500">Choir</span>
                  </div>
                  <div className="flex items-center justify-between gap-1 rounded-lg bg-slate-50 px-2 py-1.5 text-xs">
                    <span className="truncate">Grace Lian</span><span className="shrink-0 text-slate-500">Usher</span>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white/95 p-3">
                <p className="text-xs font-medium text-slate-500">Attendance chart</p>
                <div className="mt-4 flex h-[96px] items-end gap-4">
                  <div className="w-3 rounded-t bg-[#dbe7f8]" style={{ height: "38%" }} />
                  <div className="w-3 rounded-t bg-[#c8dbf4]" style={{ height: "55%" }} />
                  <div className="w-3 rounded-t bg-[#b4cff0]" style={{ height: "67%" }} />
                  <div className="w-3 rounded-t bg-[#9dc0ea]" style={{ height: "82%" }} />
                  <div className="w-3 rounded-t bg-[#7da9e1]" style={{ height: "74%" }} />
                  <div className="w-3 rounded-t bg-[#5f95d9]" style={{ height: "90%" }} />
                  <div className="w-3 rounded-t bg-[#5f95d9]" style={{ height: "80%" }} />
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white/95 p-3">
                <p className="text-xs font-medium text-slate-500">Finance summary</p>
                <p className="mt-3 text-lg font-semibold tracking-tight text-slate-900">$24,820</p>
                <p className="text-xs text-emerald-600">+12.4% this month</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white/95 p-3">
                <p className="text-xs font-medium text-slate-500">Ministries</p>
                <div className="mt-3 space-y-2 text-xs text-slate-700">
                  <p className="truncate rounded-md bg-slate-50 px-2 py-1">Worship Team</p>
                  <p className="truncate rounded-md bg-slate-50 px-2 py-1">Children Ministry</p>
                  <p className="truncate rounded-md bg-slate-50 px-2 py-1">Youth Fellowship</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 pb-10">
          <div className="rounded-2xl border border-[#dce5f0] bg-gradient-to-r from-[#f7fbff] to-[#f8f3eb] p-6 md:p-8">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-[#1e3a8a]">Churches on iChurch Data</h2>
            <p className="mt-2 text-center text-sm text-slate-600">Churches already using the platform to manage their members and ministries.</p>
            <div className="mt-5 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
              <div className="animate-marquee flex gap-4 w-max">
                {[...TESTIMONIAL_CHURCHES, ...TESTIMONIAL_CHURCHES].map((church, i) => (
                  <article key={i} className="w-[280px] shrink-0 rounded-xl border border-[#dce5f0] bg-white p-5 shadow-sm text-center">
                    <div className="mb-3 flex justify-center">
                      <img
                        src={church.logo}
                        alt={`${church.name} logo`}
                        className="h-20 w-20 rounded-full border border-slate-200 object-cover grayscale-0 saturate-150 contrast-110"
                        loading="lazy"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = "/placeholder.svg";
                        }}
                      />
                    </div>
                    <p className="text-base font-semibold text-slate-900">{church.name}</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">"{church.quote}"</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-6 md:py-6">
          <div className="rounded-2xl border border-[#d9e3ef] bg-gradient-to-r from-[#f8fbff] to-[#f6f9f4] p-6 md:p-6">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-[#1e3a8a]">"Church management shouldn't be complicated."</h2>
            <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {PAIN_POINTS.map((item) => (
                <article key={item.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#feeecf] to-[#f8e6d5]">
                    <item.icon className="h-5 w-5 text-[#1e3a8a]" />
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="scroll-mt-20 mx-auto w-full max-w-6xl px-4 py-12">
          <div className="rounded-2xl border border-[#d9e3ef] bg-gradient-to-r from-[#f8fbff] to-[#f4f8f1] p-6 md:p-8">
            <h2 className="text-center text-2xl font-semibold tracking-tight text-[#1e3a8a]">See how simple church management can be</h2>
            <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {FEATURE_CARDS.map((feature) => (
                <article key={feature.title} className="rounded-xl border border-[#dce5f0] bg-gradient-to-b from-white to-[#fbfdff] p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#e7effc] to-[#f4efe8]">
                    <feature.icon className="h-5 w-5 text-[#1e3a8a]" />
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">{feature.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section id="pricing" className="scroll-mt-20 mx-auto w-full max-w-6xl px-4 py-4">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">Select plan</h2>
            <p className="mt-2 text-sm text-slate-600">All plans include the same core features. Pick based on your church size.</p>
            <div className="mt-4 inline-flex items-center rounded-full border border-[#dce5f0] bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  billingCycle === "monthly"
                    ? "bg-[#1e3a8a] text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("yearly")}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  billingCycle === "yearly"
                    ? "bg-[#1e3a8a] text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Yearly
                <span className="ml-2 inline-flex items-center rounded-full bg-[#dff3e8] px-1.5 py-0.5 text-[11px] font-semibold text-[#2a9a62]">
                  Save {ANNUAL_DISCOUNT}%
                </span>
              </button>
            </div>
          </div>

          {/* Horizontal tier selector */}
          <div className="mt-4 mx-auto w-full max-w-5xl overflow-hidden rounded-2xl border-2 border-[#1e3a8a]">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {PRICING_TIERS.map((tier, index) => {
                const isSelected = selectedPlanIndex === index;
                const memberNumber = tier.label.split(" ")[2];
                const isSecondColumnMobile = index % 2 === 1;
                const isSecondRowMobile = index >= 2;
                const isNotFirstDesktop = index > 0;

                return (
                  <button
                    key={tier.label}
                    type="button"
                    onClick={() => setSelectedPlanIndex(index)}
                    className={`flex cursor-pointer flex-col items-center justify-center px-4 py-3 text-center transition-all duration-150 border-[#1e3a8a] ${
                      isSecondColumnMobile ? "border-l" : "border-l-0"
                    } ${
                      isSecondRowMobile ? "border-t" : "border-t-0"
                    } md:border-t-0 ${
                      isNotFirstDesktop ? "md:border-l" : "md:border-l-0"
                    } ${
                      isSelected
                        ? "bg-[#1e3a8a] text-white"
                        : "bg-white text-foreground hover:bg-[#f0f4fa]"
                    }`}
                    aria-pressed={isSelected}
                  >
                    <span className="inline-flex items-center justify-center gap-1.5 text-center">
                      <Users className={`h-3.5 w-3.5 ${isSelected ? "text-white" : "text-[#1e3a8a]"}`} />
                      <span className="text-xl font-semibold leading-tight tracking-tight">{memberNumber}</span>
                      <span className={`text-xs font-normal leading-tight ${isSelected ? "text-white/90" : "text-slate-600"}`}>
                        members
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected plan details */}
          <div className="mt-12 mx-auto w-full max-w-3xl">
            <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-[340px_minmax(0,1fr)]">
              <div className="space-y-2 text-center">
                <p className="min-h-7 whitespace-nowrap text-2xl font-semibold text-foreground leading-none">
                  {billingCycle === "monthly"
                    ? `$${PRICING_TIERS[selectedPlanIndex].monthlyPrice}`
                    : `$${Math.round(PRICING_TIERS[selectedPlanIndex].monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT / 100))}`}
                  <span className="ml-1 text-sm font-normal text-slate-500">/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                </p>

                {billingCycle === "yearly" && (
                  <p className="text-xs text-slate-500">
                    <span className="line-through">${PRICING_TIERS[selectedPlanIndex].monthlyPrice * 12}/year</span> billed annually
                    <span className="ml-2 font-semibold text-emerald-600">
                      Save ${PRICING_TIERS[selectedPlanIndex].monthlyPrice * 12 - Math.round(PRICING_TIERS[selectedPlanIndex].monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT / 100))}
                    </span>
                  </p>
                )}

                <p className="text-sm font-light text-slate-600">
                  Selected plan: {PRICING_TIERS[selectedPlanIndex].name}
                </p>

                <Button className="h-12 w-56 rounded-xl px-6 text-sm mx-auto bg-[#1e3a8a] text-white hover:bg-[#162d70]" asChild>
                  <Link to={isAuthenticated ? "/dashboard" : "/register"}>
                    {isAuthenticated ? "Go to dashboard" : "Get started"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="mx-auto w-fit space-y-3 text-left md:mx-0 md:w-auto">
                {PLAN_FEATURES.map((feature) => (
                  <p key={`${selectedPlanIndex}-${feature}`} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-[#1e3a8a]" />
                    {feature}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="faqs" className="scroll-mt-20 mx-auto w-full max-w-6xl px-4 py-10">
          <div className="rounded-2xl border border-[#d9e3ef] bg-gradient-to-r from-[#f8fbff] to-[#f5f9ef] p-6 md:p-8">
            <div className="text-center">
              <h2 className="text-2xl font-semibold tracking-tight text-[#1e3a8a]">FAQs</h2>
              <p className="mt-2 text-sm text-slate-600">Answers to common questions before you get started.</p>
            </div>
            <Accordion type="single" collapsible className="mt-6 rounded-xl border border-[#dce5f0] bg-white shadow-sm">
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem key={item.question} value={`faq-${index}`} className="px-5">
                  <AccordionTrigger className="py-4 text-left text-base font-normal text-slate-900 hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-sm leading-relaxed text-slate-600">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

      </main>

      <footer className="relative z-10 border-t border-slate-200 bg-gradient-to-b from-white to-[#f8fbff]">
        <div className="mx-auto w-full max-w-6xl px-6 py-10 text-sm text-slate-600">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-base font-semibold text-slate-900">iChurch Data LLC</p>
              <p className="mt-1">1260 Bees Ferry Rd</p>
              <p>Charleston, SC 29414</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-slate-900">Terms of Service</Link>
              <Link to="/about" className="hover:text-slate-900">About</Link>
              <Link to={isAuthenticated ? "/dashboard" : "/login"} className="hover:text-slate-900">
                {isAuthenticated ? "Dashboard" : "Sign in"}
              </Link>
            </div>
          </div>
          <div className="mt-6 border-t border-slate-200 pt-5">
            <p>© 2026 iChurch Data. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
